'use client';

import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const sections = [
  {
    id: 'overview',
    title: 'Overview',
    content: `Harrington & Associates LLP ("the Firm," "we," "us," or "our") is committed to protecting the privacy and confidentiality of information submitted through this website. This Privacy Policy explains what information we collect, how we use it, and the measures we take to safeguard it. By using this website, you agree to the practices described herein.`,
  },
  {
    id: 'information-collected',
    title: 'Information We Collect',
    items: [
      {
        label: 'Contact & Inquiry Data',
        text: 'When you submit a consultation request or contact form, we collect your name, email address, phone number, and any details you voluntarily provide regarding your legal matter.',
      },
      {
        label: 'Client Reviews',
        text: 'If you submit a review through our website, we collect your name, rating, and the text of your review.',
      },
      {
        label: 'Usage Data',
        text: 'We may collect non-personally identifiable information such as browser type, referring URLs, and pages visited to improve website performance.',
      },
      {
        label: 'reCAPTCHA Data',
        text: 'Our review submission form uses Google reCAPTCHA to prevent automated abuse. reCAPTCHA collects hardware and software information (such as device and application data) and sends it to Google for analysis. See the section below for full details.',
      },
    ],
  },
  {
    id: 'recaptcha',
    title: 'Google reCAPTCHA',
    content: `We use Google reCAPTCHA v2 on our review submission form to protect against spam and automated misuse. reCAPTCHA is operated by Google LLC and is subject to Google's Privacy Policy (https://policies.google.com/privacy) and Terms of Service (https://policies.google.com/terms).`,
    items: [
      {
        label: 'What reCAPTCHA Collects',text: 'Google reCAPTCHA collects information about your device, browser, IP address, mouse movements, and interaction patterns to determine whether you are a human user.',
      },
      {
        label: 'How It Is Used',text: 'This data is sent to Google and used solely to verify that form submissions are made by real users. We do not receive or store the raw data collected by reCAPTCHA.',
      },
      {
        label: 'Your Choices',text: 'If you prefer not to use reCAPTCHA, you may contact us directly by phone at (917) 737-4049 or by email at info@harringtonlaws.com.',
      },
    ],
  },
  {
    id: 'supabase',title: 'Data Storage — Supabase',
    content: `Information submitted through this website — including contact inquiries and client reviews — is stored securely using Supabase, a cloud database platform.`,
    items: [
      {
        label: 'Where Data Is Stored',text: 'Supabase stores data on servers hosted by Amazon Web Services (AWS). Data may be stored in the United States or other jurisdictions where AWS operates data centers.',
      },
      {
        label: 'Security Measures',text: 'Supabase employs industry-standard security practices including encryption in transit (TLS/SSL) and encryption at rest. Access to the database is restricted through row-level security policies.',
      },
      {
        label: 'Data Retention',text: 'We retain submitted data only as long as necessary to fulfill the purpose for which it was collected, or as required by applicable law. You may request deletion of your data at any time by contacting us.',
      },
      {
        label: 'Supabase Privacy Policy',text: 'For more information on how Supabase handles data, please review the Supabase Privacy Policy at https://supabase.com/privacy.',
      },
    ],
  },
  {
    id: 'use-of-information',title: 'How We Use Your Information',
    items: [
      { label: 'Respond to Inquiries', text: 'To contact you regarding your legal matter or consultation request.' },
      { label: 'Publish Reviews', text: 'To display approved client reviews on our website (with your consent).' },
      { label: 'Improve Our Website', text: 'To analyze usage patterns and enhance the user experience.' },
      { label: 'Legal Compliance', text: 'To comply with applicable laws, regulations, or legal proceedings.' },
    ],
  },
  {
    id: 'no-legal-advice',title: 'No Attorney-Client Relationship',
    content: `Submitting information through this website does not create an attorney-client relationship. Do not send confidential or privileged information through this website until an attorney-client relationship has been formally established. Communications through this site are not protected by attorney-client privilege until such a relationship is confirmed in writing.`,
  },
  {
    id: 'third-parties',title: 'Third-Party Services',
    content: `We do not sell, rent, or trade your personal information to third parties. We may share information with trusted service providers (such as Supabase and Google) solely to operate this website and its features. These providers are contractually obligated to handle your data securely and only for the purposes we specify.`,
  },
  {
    id: 'your-rights',title: 'Your Rights',
    content: `Depending on your jurisdiction, you may have the right to access, correct, or request deletion of personal information we hold about you. To exercise any of these rights, please contact us at:`,
    contact: true,
  },
  {
    id: 'changes',title: 'Changes to This Policy',
    content: `We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. The date of the most recent revision will be noted at the top of this page. Continued use of this website after any changes constitutes your acceptance of the updated policy.`,
  },
];

export default function PrivacyPolicyPage() {
  const lastUpdated = 'August 19, 2025';

  return (
    <div className="home-page">
      <Header />
      <main style={{ paddingTop: '96px', background: 'var(--paper)', minHeight: '100vh' }}>
        {/* Page Header */}
        <section
          style={{
            background: 'var(--ink)',
            color: '#fff',
            padding: '72px 0 56px',
            borderBottom: '1px solid rgba(185,152,95,0.25)',
          }}
        >
          <div className="site-container">
            <p className="eyebrow" style={{ color: 'var(--gold)', marginBottom: '16px' }}>
              Legal &amp; Compliance
            </p>
            <h1
              style={{
                fontFamily: 'var(--serif)',
                fontSize: 'clamp(36px, 5vw, 64px)',
                fontWeight: 500,
                letterSpacing: '-0.03em',
                lineHeight: 1.05,
                margin: '0 0 20px',
              }}
            >
              Privacy Policy
            </h1>
            <p style={{ color: 'rgba(236,230,219,0.7)', fontSize: '14px', fontFamily: 'var(--mono)', letterSpacing: '0.08em' }}>
              Last updated: {lastUpdated}
            </p>
          </div>
        </section>

        {/* Content */}
        <section style={{ padding: '72px 0 96px' }}>
          <div
            className="site-container"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1fr) 280px',
              gap: '64px',
              alignItems: 'start',
            }}
          >
            {/* Main Content */}
            <div>
              {sections?.map((section) => (
                <div
                  key={section?.id}
                  id={section?.id}
                  style={{
                    marginBottom: '52px',
                    paddingBottom: '52px',
                    borderBottom: '1px solid var(--line)',
                  }}
                >
                  <h2
                    style={{
                      fontFamily: 'var(--serif)',
                      fontSize: 'clamp(22px, 2.5vw, 30px)',
                      fontWeight: 500,
                      letterSpacing: '-0.02em',
                      color: 'var(--ink)',
                      marginBottom: '20px',
                    }}
                  >
                    {section?.title}
                  </h2>

                  {section?.content && (
                    <p
                      style={{
                        color: 'var(--muted)',
                        lineHeight: 1.8,
                        fontSize: '16px',
                        marginBottom: section?.items ? '24px' : '0',
                      }}
                    >
                      {section?.content}
                    </p>
                  )}

                  {section?.items && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {section?.items?.map((item) => (
                        <div
                          key={item?.label}
                          style={{
                            paddingLeft: '20px',
                            borderLeft: '2px solid var(--gold)',
                          }}
                        >
                          <p
                            style={{
                              fontFamily: 'var(--mono)',
                              fontSize: '11px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.12em',
                              color: 'var(--gold)',
                              marginBottom: '6px',
                            }}
                          >
                            {item?.label}
                          </p>
                          <p style={{ color: 'var(--muted)', lineHeight: 1.75, fontSize: '15px', margin: 0 }}>
                            {item?.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {section?.contact && (
                    <div
                      style={{
                        marginTop: '20px',
                        background: 'var(--stone)',
                        padding: '24px 28px',
                        borderLeft: '3px solid var(--gold)',
                      }}
                    >
                      <p style={{ fontFamily: 'var(--serif)', fontSize: '17px', fontWeight: 600, marginBottom: '8px', color: 'var(--ink)' }}>
                        Harrington &amp; Associates LLP
                      </p>
                      <p style={{ color: 'var(--muted)', margin: '0 0 4px', fontSize: '15px' }}>
                        350 Fifth Avenue, Suite 4100, New York, NY 10118
                      </p>
                      <a href="tel:+19177374049" style={{ color: 'var(--gold)', fontSize: '15px', display: 'block', marginBottom: '4px' }}>
                        (917) 737-4049
                      </a>
                      <a href="mailto:info@harringtonlaws.com" style={{ color: 'var(--gold)', fontSize: '15px' }}>
                        info@harringtonlaws.com
                      </a>
                    </div>
                  )}
                </div>
              ))}

              {/* Back link */}
              <Link
                href="/"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'var(--mono)',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  color: 'var(--gold)',
                  borderBottom: '1px solid var(--gold)',
                  paddingBottom: '2px',
                }}
              >
                ← Return to Home
              </Link>
            </div>

            {/* Sidebar TOC */}
            <aside
              style={{
                position: 'sticky',
                top: '120px',
                background: 'var(--ink-soft)',
                padding: '28px',
                color: '#fff',
              }}
            >
              <p
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.18em',
                  color: 'var(--gold)',
                  marginBottom: '20px',
                }}
              >
                Contents
              </p>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {sections?.map((section) => (
                  <a
                    key={section?.id}
                    href={`#${section?.id}`}
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: '11px',
                      letterSpacing: '0.08em',
                      color: 'rgba(236,230,219,0.65)',
                      transition: 'color 0.18s',
                      lineHeight: 1.5,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--gold-soft)')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(236,230,219,0.65)')}
                  >
                    {section?.title}
                  </a>
                ))}
              </nav>

              <div
                style={{
                  marginTop: '32px',
                  paddingTop: '24px',
                  borderTop: '1px solid rgba(185,152,95,0.25)',
                }}
              >
                <p
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.18em',
                    color: 'var(--gold)',
                    marginBottom: '12px',
                  }}
                >
                  Questions?
                </p>
                <p style={{ fontSize: '13px', color: 'rgba(236,230,219,0.6)', lineHeight: 1.6, marginBottom: '14px' }}>
                  Contact us directly with any privacy-related questions.
                </p>
                <a
                  href="mailto:info@harringtonlaws.com"
                  style={{
                    display: 'inline-block',
                    fontFamily: 'var(--mono)',
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    color: 'var(--ink)',
                    background: 'var(--gold)',
                    padding: '10px 16px',
                  }}
                >
                  Email Us
                </a>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
